import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import t from '../theme';

const STATUS = {
  assigned:    { color: t.blue,   bg: t.blueLight,   label: 'Assigned' },
  in_progress: { color: t.orange, bg: t.orangeLight, label: 'In Progress' },
  completed:   { color: t.green,  bg: t.greenLight,  label: 'Completed' },
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.assigned;
  return (
    <span style={{ background: s.bg, color: s.color, padding: '3px 10px',
      borderRadius: '20px', fontSize: '11px', fontWeight: 600 }}>{s.label}</span>
  );
}

const isAud = u => u && /\.(mp3|wav|m4a|aac|ogg)$/i.test(u);
const isVid = u => u && /\.(mp4|mov|avi|mkv|webm)$/i.test(u);
const fmtSz = b => b < 1048576 ? `${(b/1024).toFixed(0)} KB` : `${(b/1048576).toFixed(1)} MB`;
const fmtDate = d => new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'});
const fmtDT = d => new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric'}) + ' ' + new Date(d).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'});

// ── MediaPlayer helper ─────────────────────────────────────────────────────
function MediaPlayer({ url, label }) {
  if (!url) return null;
  return (
    <div style={{ marginBottom: '8px' }}>
      {label && <div style={{ fontSize: '11px', color: t.text3, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>}
      {isAud(url) && <audio controls style={{ width: '100%' }}><source src={url}/></audio>}
      {isVid(url) && <video controls style={{ width: '100%', borderRadius: '6px', maxHeight: '200px' }}><source src={url}/></video>}
    </div>
  );
}

// ── FileUploadButton ────────────────────────────────────────────────────────
function FileUploadButton({ onFile, uploading, pct, label, replaceLabel }) {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const pick = f => { setFile(f); onFile(f); };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <input ref={ref} type="file" accept="audio/*,video/*" style={{ display: 'none' }}
        onChange={e => pick(e.target.files[0]||null)}/>
      <label onClick={() => ref.current?.click()}
        style={{ ...t.input, cursor: 'pointer', textAlign: 'center',
          border: `1px dashed ${t.border}`, color: file ? t.text : t.text3 }}>
        {file ? `${file.name} (${fmtSz(file.size)})` : replaceLabel || label}
      </label>
      {uploading && pct > 0 && (
        <div style={{ height: '4px', background: t.surface3, borderRadius: '2px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: t.accent, transition: 'width 0.3s'}}/>
        </div>
      )}
    </div>
  );
}


// ── CommentThread — per student thread ────────────────────────────────────
function CommentThread({ hwId, studentId, user, isAdmin, compact = false }) {
  const [comments, setComments] = useState([]);
  const [comment,  setComment]  = useState('');
  const [posting,  setPosting]  = useState(false);

  const load = useCallback(async () => {
    try {
      const params = studentId ? `?student_id=${studentId}` : '';
      const r = await axios.get(`/api/homework/${hwId}/comments${params}`);
      setComments(r.data);
    } catch(e) { console.error(e); }
  }, [hwId, studentId]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!comment.trim()) return;
    setPosting(true);
    try {
      const payload = { comment_text: comment.trim() };
      if (studentId) payload.student_id = studentId;
      const r = await axios.post(`/api/homework/${hwId}/comments`, payload);
      setComments(r.data); setComment('');
    } catch(e) { console.error(e); }
    finally { setPosting(false); }
  };

  const del = async (id) => {
    try {
      await axios.delete(`/api/homework/${hwId}/comments/${id}`);
      load();
    } catch(e) { console.error(e); }
  };

  return (
    <div style={{ marginTop: compact ? '10px' : '0', paddingTop: compact ? '10px' : '0',
      borderTop: compact ? `1px solid ${t.border}` : 'none' }}>
      {!compact && <div style={{ ...t.label, marginBottom: '10px' }}>Comments ({comments.length})</div>}
      {compact && <div style={{ fontSize: '11px', color: t.text3, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Thread ({comments.length})</div>}

      {/* Comment input */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
        <div style={{ width: '26px', height: '26px', borderRadius: '6px', background: t.accentLight,
          border: `1px solid ${t.accentBorder}`, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: '11px', fontWeight: 600, color: t.accent, flexShrink: 0 }}>
          {user?.username?.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, display: 'flex', gap: '6px' }}>
          <input style={{ ...t.input, flex: 1, padding: '6px 10px', fontSize: '12px' }}
            placeholder="Leave a comment..." value={comment}
            onChange={e => setComment(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && post()}/>
          <button style={{ ...t.btnPrimary, padding: '6px 12px', fontSize: '12px', flexShrink: 0 }}
            onClick={post} disabled={posting || !comment.trim()}>
            {posting ? '...' : 'Post'}
          </button>
        </div>
      </div>

      {/* Comments list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: compact ? '180px' : 'none', overflowY: compact ? 'auto' : 'visible' }}>
        {!comments.length
          ? <p style={{ fontSize: '12px', color: t.text3, fontStyle: 'italic', margin: 0 }}>No comments yet.</p>
          : comments.map(c => (
            <div key={c.id} style={{ display: 'flex', gap: '8px' }}>
              <div style={{ width: '24px', height: '24px', borderRadius: '5px', flexShrink: 0,
                background: c.role === 'admin' ? t.accentLight : t.surface3,
                border: `1px solid ${c.role === 'admin' ? t.accentBorder : t.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '10px', fontWeight: 600, color: c.role === 'admin' ? t.accent : t.text2 }}>
                {c.username?.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, background: t.surface2, borderRadius: '6px', padding: '8px 10px', border: `1px solid ${t.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 500, color: t.text }}>{c.username?.charAt(0).toUpperCase() + c.username?.slice(1)}</span>
                  {c.role === 'admin' && <span style={{ fontSize: '10px', background: t.accentLight, color: t.accent, padding: '1px 5px', borderRadius: '4px', fontWeight: 600 }}>Teacher</span>}
                  <span style={{ fontSize: '10px', color: t.text3, marginLeft: 'auto' }}>{fmtDT(c.createdAt)}</span>
                </div>
                <p style={{ fontSize: '12px', color: t.text2, margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{c.comment_text}</p>
                {(isAdmin || c.user_id === user?.id) && (
                  <button style={{ background: 'none', border: 'none', color: t.red, fontSize: '10px',
                    cursor: 'pointer', fontFamily: t.font, marginTop: '3px', padding: 0 }}
                    onClick={() => del(c.id)}>Delete</button>
                )}
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}

// ── Homework Detail Modal ──────────────────────────────────────────────────
function HomeworkDetailModal({ hw, onClose, onRefresh }) {
  const { user, isAdmin } = useAuth();
  const [comments,   setComments]   = useState([]);
  const [comment,    setComment]    = useState('');
  const [posting,    setPosting]    = useState(false);
  const [subFile,    setSubFile]    = useState(null);
  const [subNotes,   setSubNotes]   = useState('');
  const [subUpl,     setSubUpl]     = useState(false);
  const [subPct,     setSubPct]     = useState(0);
  const [subErr,     setSubErr]     = useState('');
  const [teachFiles, setTeachFiles] = useState({});   // { studentId: File }
  const [teachUpl,   setTeachUpl]   = useState({});   // { studentId: bool }
  const [teachPct,   setTeachPct]   = useState({});   // { studentId: 0-100 }
  const [grading,    setGrading]    = useState({});

  const myRecord = hw.myRecord;

  const fetchComments = useCallback(async (studentId) => {
    try {
      const params = studentId ? `?student_id=${studentId}` : '';
      const r = await axios.get(`/api/homework/${hw.id}/comments${params}`);
      setComments(r.data);
    }
    catch(e) { console.error(e); }
  }, [hw.id]);

  // For students, fetch their own thread on mount
  const { user: authUser, isAdmin: adminCheck } = useAuth();
  useEffect(() => {
    if (!adminCheck) fetchComments(authUser?.student?.id);
  }, [fetchComments, adminCheck, authUser]);

  const postComment = async (studentId) => {
    if (!comment.trim()) return;
    setPosting(true);
    try {
      const payload = { comment_text: comment.trim() };
      if (studentId) payload.student_id = studentId;
      const r = await axios.post(`/api/homework/${hw.id}/comments`, payload);
      setComments(r.data); setComment('');
    } catch(e) { console.error(e); }
    finally { setPosting(false); }
  };

  const deleteComment = async id => {
    try { await axios.delete(`/api/homework/${hw.id}/comments/${id}`); fetchComments(); }
    catch(e) { console.error(e); }
  };

  // Student submission
  const handleSubmit = async () => {
    setSubErr('');
    if (!subFile && !subNotes.trim()) return setSubErr('Add a file or notes to submit.');
    setSubUpl(true);
    try {
      const fd = new FormData();
      if (subFile) fd.append('file', subFile);
      if (subNotes.trim()) fd.append('submission_notes', subNotes.trim());
      const sid = user.student?.id;
      await axios.post(`/api/homework/${hw.id}/students/${sid}/submit`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: e => setSubPct(Math.round(e.loaded*100/e.total)),
      });
      setSubFile(null); setSubNotes(''); setSubPct(0); onRefresh();
    } catch(e) { setSubErr(e.response?.data?.error || 'Upload failed.'); }
    finally { setSubUpl(false); }
  };

  // Teacher upload to student
  const handleTeacherUpload = async (studentId) => {
    const file = teachFiles[studentId];
    if (!file) return;
    setTeachUpl(u => ({ ...u, [studentId]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`/api/homework/${hw.id}/students/${studentId}/teacher-upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: e => setTeachPct(p => ({ ...p, [studentId]: Math.round(e.loaded*100/e.total) })),
      });
      setTeachFiles(f => ({ ...f, [studentId]: null }));
      onRefresh();
    } catch(e) { console.error(e); }
    finally { setTeachUpl(u => ({ ...u, [studentId]: false })); }
  };

  const handleTeacherRemove = async (studentId) => {
    try { await axios.delete(`/api/homework/${hw.id}/students/${studentId}/teacher-upload`); onRefresh(); }
    catch(e) { console.error(e); }
  };

  const handleGrade = async (studentId, updates) => {
    try { await axios.put(`/api/homework/${hw.id}/students/${studentId}`, updates); onRefresh(); }
    catch(e) { console.error(e); }
  };

  return (
    <div style={t.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...t.modal, maxWidth: '720px', maxHeight: '90vh' }}>

        {/* Header */}
        <div style={t.modalHeader}>
          <div>
            <h3 style={t.modalTitle}>{hw.title}</h3>
            <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
              {hw.taal       && <span style={{ background: t.accentLight, border: `1px solid ${t.accentBorder}`, color: t.accent, padding: '2px 8px', borderRadius: '20px', fontSize: '11px' }}>{hw.taal.name}</span>}
              {hw.lessonType && <span style={{ background: t.surface2, border: `1px solid ${t.border}`, color: t.text3, padding: '2px 8px', borderRadius: '20px', fontSize: '11px' }}>{hw.lessonType.name}</span>}
              {hw.due_date   && <span style={{ fontSize: '11px', color: t.text3 }}>Due: {fmtDate(hw.due_date)}</span>}
            </div>
          </div>
          <button style={t.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={{ ...t.modalBody, gap: '20px' }}>

          {/* ── Instructions ── */}
          {(hw.comments || hw.description) && (
            <div style={{ background: t.surface2, borderRadius: t.r, padding: '14px', border: `1px solid ${t.border}` }}>
              <div style={{ ...t.label, marginBottom: '6px' }}>Instructions</div>
              <p style={{ fontSize: '13px', color: t.text2, margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {hw.comments || hw.description}
              </p>
            </div>
          )}

          {/* ── STUDENT VIEW ── */}
          {!isAdmin && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Teacher file */}
              {myRecord?.teacher_audio_s3_key && (
                <div style={{ background: t.surface2, borderRadius: t.r, padding: '14px', border: `1px solid ${t.accentBorder}` }}>
                  <div style={{ ...t.label, marginBottom: '8px', color: t.accent }}>📎 Teacher's Reference File</div>
                  <MediaPlayer url={myRecord.teacher_audio_s3_key}/>
                </div>
              )}

              {/* My submission */}
              <div style={{ background: t.surface2, borderRadius: t.r, padding: '14px', border: `1px solid ${t.border}` }}>
                <div style={{ ...t.label, marginBottom: '10px' }}>My Submission</div>

                {myRecord?.submission_audio_s3_key && (
                  <div style={{ marginBottom: '12px' }}>
                    <MediaPlayer url={myRecord.submission_audio_s3_key} label="Current submission"/>
                    {myRecord.submission_notes && <p style={{ fontSize: '12px', color: t.text2, margin: '6px 0 0', fontStyle: 'italic' }}>{myRecord.submission_notes}</p>}
                  </div>
                )}

                {myRecord?.score != null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '12px', color: t.text3 }}>Score:</span>
                    <span style={{ background: t.accentLight, border: `1px solid ${t.accentBorder}`, color: t.accent, padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600 }}>{myRecord.score}/10</span>
                    <StatusBadge status={myRecord.status}/>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <FileUploadButton
                    onFile={setSubFile}
                    uploading={subUpl} pct={subPct}
                    label="↑ Upload submission (audio/video)"
                    replaceLabel={myRecord?.submission_audio_s3_key ? '↑ Replace submission' : null}
                  />
                  <textarea style={{ ...t.textarea, resize: 'vertical' }} rows={2} value={subNotes}
                    onChange={e => setSubNotes(e.target.value)} placeholder="Notes (optional)..."/>
                  {subErr && <div style={t.errorBox}>{subErr}</div>}
                  <button style={{ ...t.btnPrimary, alignSelf: 'flex-start' }} onClick={handleSubmit} disabled={subUpl}>
                    {subUpl ? `Uploading ${subPct}%` : myRecord?.submission_audio_s3_key ? 'Update Submission' : 'Submit'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── ADMIN VIEW — per-student panel ── */}
          {isAdmin && (
            <div>
              <div style={{ ...t.label, marginBottom: '12px' }}>
                Student Responses ({hw.students?.length || 0} assigned)
              </div>
              {(!hw.students || hw.students.length === 0) ? (
                <p style={{ fontSize: '13px', color: t.text3, fontStyle: 'italic' }}>No students assigned.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {hw.students.map(hs => {
                    const junc = hs.HomeworkStudent || {};
                    const sid = hs.id;
                    return (
                      <div key={sid} style={{ background: t.surface2, borderRadius: t.r, padding: '14px', border: `1px solid ${t.border}` }}>

                        {/* Student header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: t.accentLight,
                              border: `1px solid ${t.accentBorder}`, display: 'flex', alignItems: 'center',
                              justifyContent: 'center', fontSize: '12px', fontWeight: 600, color: t.accent }}>
                              {hs.full_name?.[0]}
                            </div>
                            <span style={{ fontSize: '14px', fontWeight: 500, color: t.text }}>{hs.full_name}</span>
                          </div>
                          <StatusBadge status={junc.status || 'assigned'}/>
                        </div>

                        {/* Two columns: student submission | teacher upload */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px,100%), 1fr))', gap: '12px' }}>

                          {/* Student submission */}
                          <div style={{ background: t.surface3, borderRadius: '8px', padding: '10px' }}>
                            <div style={{ fontSize: '11px', color: t.text3, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Student Submission</div>
                            {junc.submission_audio_s3_key ? (
                              <>
                                <MediaPlayer url={junc.submission_audio_s3_key}/>
                                {junc.submission_notes && <p style={{ fontSize: '12px', color: t.text2, margin: '4px 0 0', fontStyle: 'italic' }}>{junc.submission_notes}</p>}
                              </>
                            ) : (
                              <p style={{ fontSize: '12px', color: t.text3, fontStyle: 'italic', margin: 0 }}>No submission yet</p>
                            )}
                          </div>

                          {/* Teacher upload */}
                          <div style={{ background: t.surface3, borderRadius: '8px', padding: '10px' }}>
                            <div style={{ fontSize: '11px', color: t.accent, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Your File for Student</div>
                            {junc.teacher_audio_s3_key ? (
                              <>
                                <MediaPlayer url={junc.teacher_audio_s3_key}/>
                                <button style={{ ...t.btnDanger, padding: '4px 10px', fontSize: '11px', marginTop: '4px' }}
                                  onClick={() => handleTeacherRemove(sid)}>Remove</button>
                              </>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <FileUploadButton
                                  onFile={f => setTeachFiles(tf => ({ ...tf, [sid]: f }))}
                                  uploading={teachUpl[sid]} pct={teachPct[sid]||0}
                                  label="↑ Upload for student"
                                />
                                {teachFiles[sid] && (
                                  <button style={{ ...t.btnPrimary, padding: '6px 12px', fontSize: '12px' }}
                                    onClick={() => handleTeacherUpload(sid)} disabled={teachUpl[sid]}>
                                    {teachUpl[sid] ? `Uploading ${teachPct[sid]||0}%` : 'Upload'}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Grading row */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '10px', paddingTop: '10px', borderTop: `1px solid ${t.border}` }}>
                          <select style={{ ...t.select, padding: '5px 10px', fontSize: '12px', width: 'auto' }}
                            value={grading[sid]?.status || junc.status || 'assigned'}
                            onChange={e => {
                              const s = e.target.value;
                              setGrading(g => ({ ...g, [sid]: { ...g[sid], status: s } }));
                              handleGrade(sid, { status: s });
                            }}>
                            <option value="assigned">Assigned</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                          </select>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input type="number" min="0" max="10"
                              style={{ ...t.input, width: '64px', padding: '5px 10px', fontSize: '12px' }}
                              placeholder="Score"
                              defaultValue={junc.score || ''}
                              onBlur={e => handleGrade(sid, { score: e.target.value ? parseInt(e.target.value) : null })}/>
                            <span style={{ fontSize: '11px', color: t.text3 }}>/10</span>
                          </div>
                        </div>

                        {/* Per-student comment thread */}
                        <CommentThread
                          hwId={hw.id}
                          studentId={sid}
                          user={user}
                          isAdmin={isAdmin}
                          compact={true}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Comments — student view shows own thread only ── */}
          {!isAdmin && (
            <CommentThread
              hwId={hw.id}
              studentId={user?.student?.id}
              user={user}
              isAdmin={isAdmin}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main HomeworkPage ──────────────────────────────────────────────────────
export default function HomeworkPage() {
  const { isAdmin } = useAuth();
  const [homework,  setHomework]  = useState([]);
  const [students,  setStudents]  = useState([]);
  const [taals,     setTaals]     = useState([]);
  const [types,     setTypes]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [detailHw,  setDetailHw]  = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editHw,    setEditHw]    = useState(null);
  const [search,    setSearch]    = useState('');
  const [filterTaal,   setFilterTaal]   = useState('');
  const [filterType,   setFilterType]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStudent,setFilterStudent]= useState('');
  const [groupBy,   setGroupBy]   = useState('all');
  const [display,   setDisplay]   = useState(() => localStorage.getItem('hwDisplay') || 'list');
  const [form,      setForm]      = useState({ title:'', taal_id:'', type_id:'', date_assigned:'', due_date:'', comments:'', student_ids:[] });
  const [formErr,   setFormErr]   = useState('');
  const [saving,    setSaving]    = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const refreshDetail = useCallback(async (hwId) => {
    try {
      const r = await axios.get('/api/homework');
      const updated = r.data.find(h => h.id === hwId);
      if (updated) setDetailHw(updated);
      setHomework(r.data);
    } catch(e) { console.error(e); }
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [hr, tr, tyr] = await Promise.all([
        axios.get('/api/homework'),
        axios.get('/api/taals'),
        axios.get('/api/types'),
      ]);
      setHomework(hr.data); setTaals(tr.data); setTypes(tyr.data);
      if (isAdmin) {
        const sr = await axios.get('/api/students');
        setStudents(sr.data);
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setDisplayMode = m => { setDisplay(m); localStorage.setItem('hwDisplay', m); };

  const filtered = homework.filter(hw => {
    if (search && !hw.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterTaal && String(hw.taal_id) !== filterTaal) return false;
    if (filterType && String(hw.type_id) !== filterType) return false;
    if (filterStudent) {
      const assigned = hw.students?.some(s => String(s.id) === filterStudent);
      if (!assigned) return false;
    }
    if (filterStatus === 'overdue') {
      const isOverdue = hw.due_date && new Date(hw.due_date) < new Date();
      const allDone = hw.students?.every(s => s.HomeworkStudent?.status === 'completed');
      if (!isOverdue || allDone) return false;
    }
    return true;
  });

  const submissionDot = (hw) => {
    if (!hw.students?.length) return t.text3;
    const total = hw.students.length;
    const submitted = hw.students.filter(s => s.HomeworkStudent?.submission_audio_s3_key).length;
    const overdue = hw.due_date && new Date(hw.due_date) < new Date();
    if (submitted === total) return t.green;
    if (submitted > 0) return t.orange;
    if (overdue) return t.red;
    return t.text3;
  };

  const openAdd = () => {
    setEditHw(null);
    setForm({ title:'', taal_id:'', type_id:'', date_assigned:'', due_date:'', comments:'', student_ids:[] });
    setFormErr(''); setModalOpen(true);
  };

  const openEdit = hw => {
    setEditHw(hw);
    setForm({
      title:         hw.title,
      taal_id:       String(hw.taal_id||''),
      type_id:       String(hw.type_id||''),
      date_assigned: hw.date_assigned||'',
      due_date:      hw.due_date||'',
      comments:      hw.comments||hw.description||'',
      student_ids:   hw.students?.map(s=>s.id).filter(Boolean)||[],
    });
    setFormErr(''); setModalOpen(true);
  };

  const toggleStudent = id => setForm(f => ({
    ...f, student_ids: f.student_ids.includes(id) ? f.student_ids.filter(x=>x!==id) : [...f.student_ids, id],
  }));

  const handleSave = async () => {
    setFormErr('');
    if (!form.title.trim()) return setFormErr('Title is required.');
    setSaving(true);
    try {
      const payload = { ...form, taal_id: form.taal_id||null, type_id: form.type_id||null };
      if (editHw) await axios.put(`/api/homework/${editHw.id}`, payload);
      else        await axios.post('/api/homework', payload);
      setModalOpen(false); fetchAll();
    } catch(e) { setFormErr(e.response?.data?.error||'Failed to save.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await axios.delete(`/api/homework/${deleteTarget.id}`); setDeleteTarget(null); fetchAll(); }
    catch(e) { console.error(e); }
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'24px', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <h2 style={{ fontSize:'22px', fontWeight:600, color:t.text, letterSpacing:'-0.3px', margin:0 }}>
            {isAdmin ? 'Homework (Ryaaz)' : 'My Homework'}
          </h2>
          <p style={{ fontSize:'13px', color:t.text3, margin:'4px 0 0' }}>{homework.length} assignment{homework.length!==1?'s':''}</p>
        </div>
        {isAdmin && <button style={t.btnPrimary} onClick={openAdd}>+ New Assignment</button>}
      </div>

      {/* Search + Filters */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'16px', flexWrap:'wrap' }}>
        <input style={{ ...t.input, flex:1, minWidth:'120px', maxWidth:'240px' }}
          placeholder="🔍 Search..." value={search} onChange={e => setSearch(e.target.value)}/>
        <select style={{ ...t.select, width:'auto', minWidth:'120px' }}
          value={filterTaal} onChange={e => setFilterTaal(e.target.value)}>
          <option value="">All Taals</option>
          {taals.map(tl => <option key={tl.id} value={String(tl.id)}>{tl.name}</option>)}
        </select>
        <select style={{ ...t.select, width:'auto', minWidth:'120px' }}
          value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {types.map(tp => <option key={tp.id} value={String(tp.id)}>{tp.name}</option>)}
        </select>
        <select style={{ ...t.select, width:'auto', minWidth:'120px' }}
          value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="overdue">Overdue</option>
        </select>
        {isAdmin && (
          <select style={{ ...t.select, width:'auto', minWidth:'140px' }}
            value={filterStudent} onChange={e => setFilterStudent(e.target.value)}>
            <option value="">All Students</option>
            {students.map(s => <option key={s.id} value={String(s.id)}>{s.full_name}</option>)}
          </select>
        )}
        {(search || filterTaal || filterType || filterStatus || filterStudent) && (
          <button style={{ ...t.btnSecondary, padding:'8px 14px', fontSize:'12px' }}
            onClick={() => { setSearch(''); setFilterTaal(''); setFilterType(''); setFilterStatus(''); setFilterStudent(''); }}>
            Clear
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign:'center', color:t.text3, padding:'48px' }}>Loading...</div>
      ) : filtered.length === 0 && !loading ? (
        <div style={{ ...t.card, padding:'48px', textAlign:'center' }}>
          <div style={{ fontSize:'32px', marginBottom:'12px', opacity:0.3 }}>◉</div>
          <p style={{ fontSize:'15px', fontWeight:500, color:t.text, marginBottom:'6px' }}>No assignments yet</p>
          {isAdmin && <button style={{ ...t.btnPrimary, marginTop:'8px' }} onClick={openAdd}>+ Create First Assignment</button>}
        </div>
      ) : (
        <>
          {/* View mode toggle */}
          <div style={{ display:'flex', justifyContent:'flex-end', gap:'6px', marginBottom:'12px' }}>
            {[
              { key:'list',  icon:'≡' },
              { key:'grid',  icon:'⊞' },
              { key:'table', icon:'⊟' },
            ].map(({ key, icon }) => (
              <button key={key} onClick={() => setDisplayMode(key)}
                style={{ width:'32px', height:'32px', borderRadius:'8px', border:`1px solid ${t.border}`,
                  background: display===key ? t.accent : t.surface2,
                  color: display===key ? '#0e0e0d' : t.text3,
                  cursor:'pointer', fontSize:'16px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                {icon}
              </button>
            ))}
          </div>

          {/* Grid view */}
          {display === 'grid' && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(min(280px,100%),1fr))', gap:'12px' }}>
              {filtered.map(hw => {
                const myRec = hw.myRecord;
                const overdue = hw.due_date && new Date(hw.due_date) < new Date() && myRec?.status !== 'completed';
                const submittedCount = hw.students?.filter(s => s.HomeworkStudent?.submission_audio_s3_key).length || 0;
                const dot = submissionDot(hw);
                return (
                  <div key={hw.id} style={{ ...t.card, padding:'16px', display:'flex', flexDirection:'column', gap:'10px' }}>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'8px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', flex:1 }}>
                        {isAdmin && <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:dot, flexShrink:0, marginTop:'3px' }}/>}
                        <h3 style={{ fontSize:'14px', fontWeight:500, color:t.text, margin:0 }}>{hw.title}</h3>
                      </div>
                      {myRec && <StatusBadge status={myRec.status}/>}
                    </div>
                    <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
                      {hw.taal       && <span style={{ background:t.accentLight, border:`1px solid ${t.accentBorder}`, color:t.accent, padding:'2px 8px', borderRadius:'20px', fontSize:'11px' }}>{hw.taal.name}</span>}
                      {hw.lessonType && <span style={{ background:t.surface2, border:`1px solid ${t.border}`, color:t.text3, padding:'2px 8px', borderRadius:'20px', fontSize:'11px' }}>{hw.lessonType.name}</span>}
                      {overdue && <span style={{ background:t.redLight, color:t.red, padding:'2px 8px', borderRadius:'20px', fontSize:'11px', fontWeight:600 }}>Overdue</span>}
                    </div>
                    <div style={{ fontSize:'12px', color:t.text3 }}>
                      {hw.due_date && <span style={{ color: overdue ? t.red : t.text3 }}>Due: {fmtDate(hw.due_date)}</span>}
                      {isAdmin && <span style={{ marginLeft:'8px', color: submittedCount > 0 ? t.green : t.text3 }}>↑ {submittedCount}/{hw.students?.length||0} submitted</span>}
                    </div>
                    {isAdmin && hw.students?.length > 0 && (
                      <div style={{ fontSize:'11px', color:t.text3, paddingTop:'6px', borderTop:`1px solid ${t.border}` }}>
                        {hw.students.slice(0,3).map(s=>s.full_name).join(', ')}{hw.students.length>3?` +${hw.students.length-3} more`:''}
                      </div>
                    )}
                    <div style={{ display:'flex', gap:'6px', marginTop:'auto' }}>
                      <button style={{ ...t.btnSecondary, flex:1, fontSize:'12px', padding:'7px' }} onClick={() => setDetailHw(hw)}>{isAdmin ? 'View / Grade' : 'Open'}</button>
                      {isAdmin && <>
                        <button style={{ ...t.btnSecondary, padding:'7px 10px', fontSize:'12px' }} onClick={()=>openEdit(hw)}>Edit</button>
                        <button style={{ ...t.btnDanger, padding:'7px 10px', fontSize:'12px' }} onClick={()=>setDeleteTarget(hw)}>Del</button>
                      </>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* List view */}
          {display === 'list' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {filtered.map(hw => {
                const myRec = hw.myRecord;
                const overdue = hw.due_date && new Date(hw.due_date) < new Date() && myRec?.status !== 'completed';
                const submittedCount = hw.students?.filter(s => s.HomeworkStudent?.submission_audio_s3_key).length || 0;
                const dot = submissionDot(hw);
                return (
                  <div key={hw.id} style={{ ...t.card, padding:'14px 18px', display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                    {isAdmin && <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:dot, flexShrink:0 }}/>}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px', flexWrap:'wrap' }}>
                        <h3 style={{ fontSize:'14px', fontWeight:500, color:t.text, margin:0 }}>{hw.title}</h3>
                        {myRec && <StatusBadge status={myRec.status}/>}
                        {overdue && <span style={{ background:t.redLight, color:t.red, padding:'2px 8px', borderRadius:'20px', fontSize:'11px', fontWeight:600 }}>Overdue</span>}
                      </div>
                      <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center' }}>
                        {hw.taal       && <span style={{ background:t.accentLight, border:`1px solid ${t.accentBorder}`, color:t.accent, padding:'1px 8px', borderRadius:'20px', fontSize:'11px' }}>{hw.taal.name}</span>}
                        {hw.lessonType && <span style={{ background:t.surface2, border:`1px solid ${t.border}`, color:t.text3, padding:'1px 8px', borderRadius:'20px', fontSize:'11px' }}>{hw.lessonType.name}</span>}
                        {hw.due_date   && <span style={{ fontSize:'11px', color: overdue ? t.red : t.text3 }}>Due: {fmtDate(hw.due_date)}</span>}
                        {isAdmin && <span style={{ fontSize:'11px', color: submittedCount > 0 ? t.green : t.text3 }}>↑ {submittedCount}/{hw.students?.length||0}</span>}
                        {isAdmin && hw.students?.length > 0 && <span style={{ fontSize:'11px', color:t.text3 }}>· {hw.students.slice(0,2).map(s=>s.full_name).join(', ')}{hw.students.length>2?` +${hw.students.length-2}`:''}</span>}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:'6px', flexShrink:0 }}>
                      <button style={{ ...t.btnSecondary, padding:'6px 12px', fontSize:'12px' }} onClick={() => setDetailHw(hw)}>{isAdmin ? 'View / Grade' : 'Open'}</button>
                      {isAdmin && <>
                        <button style={{ ...t.btnSecondary, padding:'6px 10px', fontSize:'12px' }} onClick={()=>openEdit(hw)}>Edit</button>
                        <button style={{ ...t.btnDanger, padding:'6px 10px', fontSize:'12px' }} onClick={()=>setDeleteTarget(hw)}>Del</button>
                      </>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Table view */}
          {display === 'table' && (
            <div style={{ ...t.card, overflow:'hidden' }}>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'600px' }}>
                  <thead>
                    <tr>
                      {['', 'Title','Taal','Type','Due Date', isAdmin?'Students':'', isAdmin?'Submissions':'','Actions'].filter(Boolean).map((h,i) => (
                        <th key={i} style={{ padding:'10px 14px', fontSize:'11px', fontWeight:600, color:t.text3,
                          textTransform:'uppercase', letterSpacing:'0.6px', textAlign:'left',
                          borderBottom:`1px solid ${t.border}`, background:t.surface2, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(hw => {
                      const myRec = hw.myRecord;
                      const overdue = hw.due_date && new Date(hw.due_date) < new Date() && myRec?.status !== 'completed';
                      const submittedCount = hw.students?.filter(s => s.HomeworkStudent?.submission_audio_s3_key).length || 0;
                      const dot = submissionDot(hw);
                      return (
                        <tr key={hw.id} style={{ borderBottom:`1px solid ${t.border}` }}>
                          <td style={{ padding:'10px 8px 10px 14px' }}>
                            <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:dot }}/>
                          </td>
                          <td style={{ padding:'10px 14px' }}>
                            <div style={{ fontSize:'13px', fontWeight:500, color:t.text }}>{hw.title}</div>
                            <div style={{ display:'flex', gap:'4px', marginTop:'3px' }}>
                              {myRec && <StatusBadge status={myRec.status}/>}
                              {overdue && <span style={{ background:t.redLight, color:t.red, padding:'1px 6px', borderRadius:'20px', fontSize:'10px', fontWeight:600 }}>Overdue</span>}
                            </div>
                          </td>
                          <td style={{ padding:'10px 14px' }}>
                            {hw.taal && <span style={{ background:t.accentLight, color:t.accent, padding:'2px 8px', borderRadius:'20px', fontSize:'11px' }}>{hw.taal.name}</span>}
                          </td>
                          <td style={{ padding:'10px 14px' }}>
                            {hw.lessonType && <span style={{ background:t.surface2, color:t.text3, padding:'2px 8px', borderRadius:'20px', fontSize:'11px' }}>{hw.lessonType.name}</span>}
                          </td>
                          <td style={{ padding:'10px 14px', fontSize:'12px', color: overdue ? t.red : t.text3, whiteSpace:'nowrap' }}>
                            {hw.due_date ? fmtDate(hw.due_date) : '—'}
                          </td>
                          {isAdmin && <td style={{ padding:'10px 14px', fontSize:'12px', color:t.text3 }}>{hw.students?.map(s=>s.full_name).join(', ')||'—'}</td>}
                          {isAdmin && <td style={{ padding:'10px 14px', fontSize:'12px', color: submittedCount > 0 ? t.green : t.text3 }}>{submittedCount}/{hw.students?.length||0}</td>}
                          <td style={{ padding:'10px 14px' }}>
                            <div style={{ display:'flex', gap:'5px' }}>
                              <button style={{ ...t.btnSecondary, padding:'4px 10px', fontSize:'11px' }} onClick={() => setDetailHw(hw)}>{isAdmin ? 'View' : 'Open'}</button>
                              {isAdmin && <>
                                <button style={{ ...t.btnSecondary, padding:'4px 8px', fontSize:'11px' }} onClick={()=>openEdit(hw)}>Edit</button>
                                <button style={{ ...t.btnDanger, padding:'4px 8px', fontSize:'11px' }} onClick={()=>setDeleteTarget(hw)}>Del</button>
                              </>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {detailHw && (
        <HomeworkDetailModal hw={detailHw} onClose={() => setDetailHw(null)}
          onRefresh={() => refreshDetail(detailHw?.id)}/>
      )}

      {/* Add/Edit modal */}
      {modalOpen && (
        <div style={t.overlay} onClick={e=>e.target===e.currentTarget&&setModalOpen(false)}>
          <div style={t.modal}>
            <div style={t.modalHeader}>
              <h3 style={t.modalTitle}>{editHw ? 'Edit Assignment' : 'New Assignment'}</h3>
              <button style={t.closeBtn} onClick={()=>setModalOpen(false)}>✕</button>
            </div>
            <div style={t.modalBody}>
              <div style={t.fieldGroup}>
                <label style={t.label}>Title *</label>
                <input style={t.input} value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Teental Kaida Practice"/>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                <div style={t.fieldGroup}>
                  <label style={t.label}>Taal</label>
                  <select style={t.select} value={form.taal_id} onChange={e=>setForm(f=>({...f,taal_id:e.target.value}))}>
                    <option value="">— None —</option>
                    {taals.map(tl=><option key={tl.id} value={String(tl.id)}>{tl.name}</option>)}
                  </select>
                </div>
                <div style={t.fieldGroup}>
                  <label style={t.label}>Type</label>
                  <select style={t.select} value={form.type_id} onChange={e=>setForm(f=>({...f,type_id:e.target.value}))}>
                    <option value="">— None —</option>
                    {types.map(tp=><option key={tp.id} value={String(tp.id)}>{tp.name}</option>)}
                  </select>
                </div>
                <div style={t.fieldGroup}>
                  <label style={t.label}>Date Assigned</label>
                  <input style={t.input} type="date" value={form.date_assigned} onChange={e=>setForm(f=>({...f,date_assigned:e.target.value}))}/>
                </div>
                <div style={t.fieldGroup}>
                  <label style={t.label}>Due Date</label>
                  <input style={t.input} type="date" value={form.due_date} onChange={e=>setForm(f=>({...f,due_date:e.target.value}))}/>
                </div>
              </div>
              <div style={t.fieldGroup}>
                <label style={t.label}>Instructions / Notes</label>
                <textarea style={t.textarea} rows={3} value={form.comments} onChange={e=>setForm(f=>({...f,comments:e.target.value}))} placeholder="Describe the assignment..."/>
              </div>
              <div style={t.fieldGroup}>
                <label style={t.label}>
                  Assign Students
                  {form.student_ids.length > 0 && <span style={{ background:t.accent, color:'#0e0e0d', borderRadius:'20px', padding:'1px 7px', fontSize:'10px', fontWeight:700, marginLeft:'6px' }}>{form.student_ids.length}</span>}
                </label>
                <div style={{ display:'flex', flexDirection:'column', gap:'5px', maxHeight:'180px', overflowY:'auto' }}>
                  {students.map(s => {
                    const on = form.student_ids.includes(s.id);
                    return (
                      <div key={s.id} onClick={()=>toggleStudent(s.id)}
                        style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px', borderRadius:'8px', cursor:'pointer',
                          border:`1px solid ${on?t.accent:t.border}`, background: on?t.accentLight:t.surface2 }}>
                        <div style={{ width:'28px', height:'28px', borderRadius:'6px', background:t.accentLight, border:`1px solid ${t.accentBorder}`,
                          display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', fontWeight:600, color:t.accent }}>
                          {s.full_name[0]}
                        </div>
                        <span style={{ fontSize:'13px', fontWeight: on?500:400, color: on?t.text:t.text2, flex:1 }}>{s.full_name}</span>
                        {on && <span style={{ color:t.accent, fontWeight:700 }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
              {formErr && <div style={t.errorBox}>{formErr}</div>}
            </div>
            <div style={t.modalFooter}>
              <button style={t.btnSecondary} onClick={()=>setModalOpen(false)}>Cancel</button>
              <button style={t.btnPrimary} onClick={handleSave} disabled={saving}>{saving?'Saving...':editHw?'Save Changes':'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div style={t.overlay} onClick={e=>e.target===e.currentTarget&&setDeleteTarget(null)}>
          <div style={{ ...t.modal, maxWidth:'400px' }}>
            <div style={t.modalHeader}>
              <h3 style={t.modalTitle}>Delete Assignment</h3>
              <button style={t.closeBtn} onClick={()=>setDeleteTarget(null)}>✕</button>
            </div>
            <div style={{ ...t.modalBody, textAlign:'center' }}>
              <p style={{ color:t.text2 }}>Delete <strong style={{ color:t.text }}>{deleteTarget.title}</strong>?</p>
            </div>
            <div style={t.modalFooter}>
              <button style={t.btnSecondary} onClick={()=>setDeleteTarget(null)}>Cancel</button>
              <button style={t.btnDanger} onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
